import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusBadge from "@/components/StatusBadge";
import { Plus, PlayCircle } from "lucide-react";

export default function HR() {
  const qc = useQueryClient();
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/hr/employees").then((r) => r.data) });
  const [monthPay, setMonthPay] = useState(new Date().toISOString().slice(0, 7));
  const { data: payroll } = useQuery({ queryKey: ["payroll", monthPay], queryFn: () => api.get(`/hr/payroll?month=${monthPay}`).then((r) => r.data) });
  const { data: attendance } = useQuery({ queryKey: ["attendance"], queryFn: () => api.get("/hr/attendance").then((r) => r.data) });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", designation: "", department: "operations", phone: "", email: "", joined_on: new Date().toISOString().slice(0, 10), monthly_salary: 25000 });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const create = useMutation({
    mutationFn: (p) => api.post("/hr/employees", p),
    onSuccess: () => { toast.success("Employee added"); setOpen(false); qc.invalidateQueries({ queryKey: ["employees"] }); },
  });

  const runPayroll = useMutation({
    mutationFn: () => api.post("/hr/payroll/run", { month: monthPay, working_days: 26 }),
    onSuccess: () => { toast.success("Payroll generated"); qc.invalidateQueries({ queryKey: ["payroll"] }); },
  });
  const pay = useMutation({
    mutationFn: (id) => api.post(`/hr/payroll/${id}/pay`),
    onSuccess: () => { toast.success("Marked paid"); qc.invalidateQueries({ queryKey: ["payroll"] }); },
  });

  const [attOpen, setAttOpen] = useState(false);
  const [att, setAtt] = useState({ employee_id: "", date: new Date().toISOString().slice(0, 10), status: "present" });
  const saveAtt = useMutation({
    mutationFn: (p) => api.post("/hr/attendance", p),
    onSuccess: () => { toast.success("Attendance recorded"); setAttOpen(false); qc.invalidateQueries(); },
  });

  return (
    <div className="space-y-4" data-testid="hr-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">HR & Payroll</h1>
        <p className="text-sm text-muted-foreground">Employees, attendance, monthly payroll.</p>
      </div>

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees">Employees ({employees?.items?.length || 0})</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="driver-attendance" data-testid="tab-driver-attendance">Driver attendance</TabsTrigger>
          <TabsTrigger value="payroll">Payroll</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="btn-new-employee"><Plus size={14} className="mr-1" /> New employee</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New employee</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="emp-name" /></div>
                  <div><Label>Designation</Label><Input value={form.designation} onChange={set("designation")} data-testid="emp-desig" /></div>
                  <div><Label>Department</Label>
                    <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["operations", "sales", "accounts", "hr", "admin"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="emp-phone" /></div>
                  <div><Label>Email</Label><Input value={form.email} onChange={set("email")} /></div>
                  <div><Label>Joined on</Label><Input type="date" value={form.joined_on} onChange={set("joined_on")} /></div>
                  <div><Label>Monthly salary ₹</Label><Input type="number" value={form.monthly_salary} onChange={set("monthly_salary")} data-testid="emp-salary" /></div>
                </div>
                <DialogFooter><Button onClick={() => create.mutate(form)} disabled={!form.name || !form.phone} data-testid="btn-save-employee">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card><CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Designation</th><th className="px-4 py-3">Dept</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3 text-right">Salary</th></tr>
              </thead>
              <tbody>
                {(employees?.items || []).map((e) => (
                  <tr key={e.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-3 font-mono font-semibold">{e.code}</td>
                    <td className="px-4 py-3">{e.name}</td>
                    <td className="px-4 py-3">{e.designation}</td>
                    <td className="px-4 py-3 text-xs uppercase">{e.department}</td>
                    <td className="px-4 py-3">{e.phone}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{e.monthly_salary.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {(!employees?.items || employees.items.length === 0) && (<tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">No employees yet.</td></tr>)}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="attendance" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Dialog open={attOpen} onOpenChange={setAttOpen}>
              <DialogTrigger asChild><Button data-testid="btn-mark-att"><Plus size={14} className="mr-1" /> Mark attendance</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Mark attendance</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Employee</Label>
                    <Select value={att.employee_id} onValueChange={(v) => setAtt({ ...att, employee_id: v })}>
                      <SelectTrigger data-testid="att-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                      <SelectContent>{(employees?.items || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={att.date} onChange={(e) => setAtt({ ...att, date: e.target.value })} /></div>
                  <div><Label>Status</Label>
                    <Select value={att.status} onValueChange={(v) => setAtt({ ...att, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["present", "absent", "half_day", "leave", "week_off"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={() => saveAtt.mutate(att)} disabled={!att.employee_id} data-testid="btn-save-att">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card><CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {(attendance?.items || []).slice(0, 30).map((a) => (
                  <tr key={a.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-3">{a.date}</td>
                    <td className="px-4 py-3">{a.employee_name}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status === "present" ? "delivered" : a.status === "absent" ? "cancelled" : "pending"} /></td>
                  </tr>
                ))}
                {(!attendance?.items || attendance.items.length === 0) && (<tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-muted-foreground">No records yet.</td></tr>)}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="driver-attendance" className="mt-4 space-y-3">
          <DriverAttendancePanel />
        </TabsContent>

        <TabsContent value="payroll" className="mt-4 space-y-3">
          <div className="flex items-end gap-2">
            <div><Label>Month</Label><Input type="month" value={monthPay} onChange={(e) => setMonthPay(e.target.value)} data-testid="payroll-month" /></div>
            <Button onClick={() => runPayroll.mutate()} disabled={runPayroll.isPending} data-testid="btn-run-payroll"><PlayCircle size={14} className="mr-1" /> Generate payroll</Button>
          </div>
          <Card><CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-3">Employee</th><th className="px-4 py-3 text-right">Days</th><th className="px-4 py-3 text-right">Salary</th><th className="px-4 py-3 text-right">Earned</th><th className="px-4 py-3 text-right">Net</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Action</th></tr>
              </thead>
              <tbody>
                {(payroll?.items || []).map((p) => (
                  <tr key={p.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-3 font-semibold">{p.employee_name}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.present_days}/{p.working_days}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{p.monthly_salary.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{p.earned_salary.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">₹{p.net_payable.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status === "paid" ? "delivered" : "pending"} /></td>
                    <td className="px-4 py-3">{p.status !== "paid" && <Button size="sm" variant="outline" onClick={() => pay.mutate(p.id)} data-testid={`pay-${p.id}`}>Mark paid</Button>}</td>
                  </tr>
                ))}
                {(!payroll?.items || payroll.items.length === 0) && (<tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No payroll for this month. Add employees and click &ldquo;Generate payroll&rdquo;.</td></tr>)}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DriverAttendancePanel() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const { data } = useQuery({
    queryKey: ["driver-attendance", from, to],
    queryFn: () => api.get(`/attendance/hr?from_date=${from}&to_date=${to}`).then((r) => r.data),
  });
  const { data: onDuty } = useQuery({
    queryKey: ["on-duty-drivers"],
    queryFn: () => api.get("/attendance/on-duty").then((r) => r.data),
    refetchInterval: 30_000,
  });
  return (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Card className="border-emerald-500/40 bg-emerald-500/5">
          <CardContent className="p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">Currently on shift</div>
            <div className="mt-1 text-3xl font-black" data-testid="on-duty-count">{onDuty?.count ?? "—"}</div>
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              {(onDuty?.items || []).slice(0, 6).map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{d.code} · {d.name}</span>
                  {d.last_lat != null && (
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${d.last_lat}&mlon=${d.last_lng}#map=17/${d.last_lat}/${d.last_lng}`}
                      target="_blank" rel="noreferrer"
                      className="font-mono text-[10px] text-primary hover:underline"
                    >
                      {d.last_lat.toFixed(4)}, {d.last_lng.toFixed(4)}
                    </a>
                  )}
                </div>
              ))}
              {onDuty?.count === 0 && <div>No drivers checked in yet.</div>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date range</div>
            <div className="flex gap-2">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="driver-att-from" />
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="driver-att-to" />
            </div>
            <div className="text-[11px] text-muted-foreground">Auto-fed from the driver app. Photos + geotags viewable per row.</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Driver</th>
                <th className="px-4 py-3">Check in</th>
                <th className="px-4 py-3">Check out</th>
                <th className="px-4 py-3">Hours</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Photo</th>
              </tr>
            </thead>
            <tbody data-testid="driver-att-table">
              {(data?.items || []).map((a) => (
                <tr key={a.id} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-3 whitespace-nowrap">{a.date}</td>
                  <td className="px-4 py-3">{a.driver_code} · {a.driver_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString() : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString() : (a.status === "on_shift" ? <span className="text-emerald-600">ON SHIFT</span> : "—")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{a.hours_worked ?? "—"}</td>
                  <td className="px-4 py-3">
                    {a.check_in_lat != null && (
                      <a href={`https://www.openstreetmap.org/?mlat=${a.check_in_lat}&mlon=${a.check_in_lng}#map=17/${a.check_in_lat}/${a.check_in_lng}`} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-primary hover:underline">
                        {a.check_in_lat.toFixed(4)}, {a.check_in_lng.toFixed(4)}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {a.check_in_photo_url && (
                      <a href={a.check_in_photo_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">View →</a>
                    )}
                  </td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No driver attendance records in this range.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
}
